import React from "react";
import { ServerResponse } from "../services/server/OldWebClient";
import { BuilderAssetService } from "../services/BuilderAssetService";
import GoogleAuthService from "../services/GoogleAuthService";
import { GoogleSlidesApiConfig } from "../entities/AppConfig";
import GoogleSlidesHelperService from "../services/GoogleSlides/GoogleSlidesHelperService";
import { ServerFailed } from "./errors/serverFailed";
import { RouteComponentProps } from "react-router-dom";
import { EditorHelper } from "./builder/editor/EditorHelper";
import { PreviewSlide } from "./builder/editor/PreviewSlide";
import GoogleSlidesRequestBuilder from "../services/GoogleSlides/GoogleSlidesRequestBuilder";
import { LoadingPage } from "../components/LoadingPage";

interface Props extends RouteComponentProps<{}> {
	config: GoogleSlidesApiConfig;
}

interface State {
	errorMessage?: string;
}

interface PersistentApplicationState {
	assetId: string;
	backgroundImagePath: string;
	fontColor: string;
}

export class GoogleSlidesOAuthLandingPage extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			errorMessage: undefined,
		};
	}

	componentDidMount() {
		if (this.props.location.hash) {
			this.redirectToGoogleSlides();
		}
	}

	static exportSlides(id: string, image: string, color: string, config: GoogleSlidesApiConfig) {
		let googleAuth = new GoogleAuthService("slides", `assetId ${id} backgroundImagePath ${image} fontColor ${color}`, config);
		googleAuth.authServiceRedirect();
	}

	getHashParams() {
		const urlHashRegex = /([^&=]+)=([^&]*)/g;

		let match;
		let hashParams: any = {};
		let { location } = this.props;

		while ((match = urlHashRegex.exec(location.hash.substr(1))) !== null) {
			hashParams[decodeURIComponent(match[1])] = decodeURIComponent(match[2]);
		}

		return hashParams;
	}

	getAccessToken() {
		let hashParams = this.getHashParams();

		if (Object.keys(hashParams).length > 0 && hashParams["error"] !== "access_denied" && hashParams["access_token"] != null) {
			return hashParams["access_token"];
		} else {
			this.setState({ errorMessage: "Error: issue authenticating with Google. Please try again later." });
			return undefined;
		}
	}

	getPersistentStateObject(params: string) {
		let splitParams = params.split(" ");
		let stateObj: PersistentApplicationState = {
			assetId: "",
			backgroundImagePath: GoogleSlidesHelperService.getDefaultBackgroundImageUrlById("16x9", this.props.config, 1),
			fontColor: "white",
		};

		for (let index = 0; index < splitParams.length; index++) {
			if (index % 2 === 0) {
				let property = splitParams[index];
				let value = splitParams[index + 1];

				if (property === "assetId") {
					stateObj.assetId = value;
				}
				if (property === "backgroundImagePath" && value !== "null") {
					stateObj.backgroundImagePath = value;
				}
				if (property === "fontColor" && value !== "null") {
					stateObj.fontColor = value;
				}
			}
		}

		return stateObj;
	}

	getPreviewSlidesFromEditorHtml(html: string) {
		let editorSlides = EditorHelper.getEditorSlides(html);
		let previewSlides = EditorHelper.getPreviewSlides(editorSlides);
		return previewSlides;
	}

	async getEditorData(assetId: string) {
		let res = await BuilderAssetService.accessAsset(assetId);

		if (ServerResponse.isError(res)) {
			if (res.statusCode === 404) {
				this.setState({ errorMessage: `404 Error: AssetId: ${assetId} not found.` });
			} else if (res.statusCode === 500) {
				this.setState({ errorMessage: `500 Error: There was an issue with the server. Please try again later.` });
			} else {
				this.setState({ errorMessage: res.message });
			}
			return null;
		} else if (ServerResponse.isModelValidation(res)) {
			this.setState({ errorMessage: "Error: There was an issue accessing the asset." });
			return null;
		}

		if (res.data !== null) {
			return res.data;
		} else {
			this.setState({ errorMessage: "No data returned from the server!" });
			return null;
		}
	}

	async createGoogleSlides(appState: PersistentApplicationState) {
		let editorData = await this.getEditorData(appState.assetId);
		if (editorData === null) {
			return null;
		}

		let previewSlides = this.getPreviewSlidesFromEditorHtml(editorData.text);
		let title = editorData.name;

		let presentationId = await this.createPresentation(title);
		if (presentationId === null) {
			return null;
		}

		presentationId = await this.addSlidesToPresentation(previewSlides, presentationId, appState);
		return presentationId;
	}

	async createPresentation(title: string) {
		let header = { Authorization: `Bearer ${this.getAccessToken()}` };
		let body = { title };
		let { config } = this.props;
		let res = await GoogleSlidesHelperService.PostToGoogleApi(config.apiUrl, body, header);
		if (res.data.error) {
			this.setState({ errorMessage: res.data.error.message });
			return null;
		}
		return res.data.presentationId as string;
	}

	async addSlidesToPresentation(slides: PreviewSlide[], presentationId: string, persistentApplicationState: PersistentApplicationState) {
		let { config } = this.props;
		let header = { Authorization: `Bearer ${this.getAccessToken()}`, Pragma: "no-cache" };
		let body = GoogleSlidesRequestBuilder.generateUpdateRequestBody(slides, persistentApplicationState, config);
		let res = await GoogleSlidesHelperService.PostToGoogleApi(`${config.apiUrl}/${presentationId}:batchUpdate`, body, header);
		if (res.data.error) {
			this.setState({ errorMessage: res.data.error.message });
			return null;
		}
		return res.data.presentationId as string;
	}

	async redirectToGoogleSlides() {
		let hashParams = this.getHashParams();
		let { config } = this.props;

		if (Object.keys(hashParams).length > 0 && hashParams["error"] !== "access_denied" && hashParams["state"] != null) {
			let appState = this.getPersistentStateObject(hashParams["state"]);
			let exportedPresentationId = await this.createGoogleSlides(appState);
			if (exportedPresentationId != null) {
				window.location.href = `${config.presentationUrl.replace("{id}", exportedPresentationId!)}`;
			}
		} else {
			this.setState({ errorMessage: "Error: issue authenticating with Google. Please try again later." });
		}
	}

	render() {
		let { errorMessage } = this.state;
		
		if(!errorMessage) 
			return <LoadingPage />
			
		return (
			<div style={{ display: "flex", height: "20em", marginTop: "10%" }}>
				<ServerFailed errorMessage={errorMessage}></ServerFailed>
			</div>
		);
	}
}
